import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef } from '@angular/core';
import { OnInit, AfterViewInit, ViewChild, ViewEncapsulation, Input } from '@angular/core';
import { CalendarEvent, CalendarEventAction, CalendarEventTimesChangedEvent, CalendarView, CalendarEventTitleFormatter } from 'angular-calendar';
import { WeekViewHourSegment } from 'calendar-utils';
import { fromEvent } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { addDays, addMinutes, endOfWeek, differenceInMinutes, startOfDay, startOfHour } from 'date-fns';
import { AgendaEventDialogComponent } from '../agenda-event-dialog/agenda-event-dialog.component';
import { MatDialog } from '@angular/material/dialog';


function floorToNearest(amount: number, precision: number) {
  return Math.floor(amount / precision) * precision;
}

function ceilToNearest(amount: number, precision: number) {
  return Math.ceil(amount / precision) * precision;
}

interface RecurringEvent {
  title: string;
  color: any;
  rrule?: {
    freq: any;
    bymonth?: number;
    bymonthday?: number;
    byweekday?: any;
  };
}

@Component({
  selector: 'app-agenda',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './agenda.component.html',
  styleUrls: ['./agenda.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class AgendaComponent implements OnInit, AfterViewInit {

  viewDate = new Date();
  events: CalendarEvent[] = [];
  dragToCreateActive = false;
  weekStartsOn: 1 = 1;
  view: CalendarView = CalendarView.Week;
  CalendarView = CalendarView;
  @Input() scrollToCurrentHour = false;
  @Input() type = 'consultation';
  @ViewChild('scrollContainer') scrollContainer: ElementRef<HTMLElement>;

  public defaultEditorContent = 'Hello World';

  constructor(
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog) { }

  ngOnInit(): void {}

  ngAfterViewInit() {
    setTimeout(() => {
      this.scrollToCurrentView();
    }, 500);
  }

  startDragToCreate(segment: WeekViewHourSegment, mouseDownEvent: MouseEvent,
                    segmentElement: HTMLElement) {
    const dragToSelectEvent: CalendarEvent = {
      id: this.events.length,
      title: this.type,
      start: segment.date,
      meta: {
      tmpEvent: true,
      },
    };
    this.events = [...this.events, dragToSelectEvent];
    const segmentPosition = segmentElement.getBoundingClientRect();
    this.dragToCreateActive = true;
    const endOfView = endOfWeek(this.viewDate, {
      weekStartsOn: this.weekStartsOn,
    });

    fromEvent(document, 'mousemove')
      .pipe(
        finalize(() => {
          delete dragToSelectEvent.meta.tmpEvent;
          dragToSelectEvent.draggable = true;
          dragToSelectEvent.resizable = {
            beforeStart: true,
            afterEnd: true,
          },
          this.dragToCreateActive = false;
          this.refresh();
        }),
        takeUntil(fromEvent(document, 'mouseup'))
      )
      .subscribe((mouseMoveEvent: MouseEvent) => {
        const minutesDiff = ceilToNearest(
          mouseMoveEvent.clientY - segmentPosition.top,
          30
        );
        const daysDiff =
        floorToNearest(
          mouseMoveEvent.clientX - segmentPosition.left,
          segmentPosition.width
        ) / segmentPosition.width;
        const newEnd = addDays(addMinutes(segment.date, minutesDiff), daysDiff);
        if (newEnd > segment.date && newEnd < endOfView) {
          dragToSelectEvent.end = newEnd;
        }
        this.refresh();
      });
  }

  clickedOnEvent(event: CalendarEvent) {
    const eventDialog = this.dialog.open(AgendaEventDialogComponent, {
      width: '700px',
      height: '500px',
      panelClass: 'custom-dialog-container', // in style.css
      data: {
        title: event.title,
        date: new Intl.DateTimeFormat('fr-FR', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric'
        }).format(event.start),
        /* TODO: load descritption of saved event */
        description: this.defaultEditorContent
      }
    });

    eventDialog.afterClosed().subscribe(result => {
      console.log(result);
    });
  }

  eventTimesChanged({event, newStart, newEnd}: CalendarEventTimesChangedEvent): void {
    this.events = this.events.map((iEvent) => {
      if (iEvent === event) {
        return {
          ...event,
          start: newStart,
          end: newEnd,
        };
      }
      return iEvent;
    });
  }

  setView(view: CalendarView) {
    this.view = view;
    console.log(this.viewDate);
  }

  private scrollToCurrentView() {
    if ((this.view === CalendarView.Week || CalendarView.Day) && this.scrollToCurrentHour) {
      // each hour is 60px high, so to get the pixels to scroll it's just the amount of minutes since midnight
      const minutesSinceStartOfDay = differenceInMinutes(
        startOfHour(new Date()),
        startOfDay(new Date())
      );
      const headerHeight = this.view === CalendarView.Week ? 60 : 0;
      this.scrollContainer.nativeElement.scrollTop =
      minutesSinceStartOfDay + headerHeight;
    }
  }

  viewChanged() {
    this.cdr.detectChanges();
    this.scrollToCurrentView();
  }

  private refresh() {
    this.events = [...this.events];
    this.cdr.detectChanges();
  }

}
